import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from './lib/api';
import { releaseLabel } from './lib/format';
import { currentMode } from './lib/palette';
import {
  DEFAULT_REGIONS, DEFAULT_ENVIRONMENTS, jobsForFilters, pickJobForRegion,
  regionOfJobId, environmentOfJobId,
} from './lib/region';
import { localityOptions } from './lib/locality';
import { resolveRunForCluster } from './lib/deeplink';
import TopBar from './components/TopBar';
import ErrorBoundary from './components/ErrorBoundary';
import { VersionChip } from './components/shared/Chip';
import PillGroup from './components/shared/PillGroup';
import TrendsScreen from './components/trends/TrendsScreen';
import PodOverviewScreen from './components/pods/PodOverviewScreen';
import IncidentsScreen from './components/incidents/IncidentsScreen';
import KpiRail from './components/KpiRail';
import PassRateChart from './components/PassRateChart';
import PodHotspot from './components/PodHotspot';
import CategoryComposition from './components/CategoryComposition';
import Leaderboard from './components/Leaderboard';
import UnaccountedFailures from './components/UnaccountedFailures';
import { IncidentBanner } from './components/IncidentsPanel';
import MasterFixPlan from './components/MasterFixPlan';

/** The whole view is addressable, so a Slack/Jira link can point at one exact state. */
function param(name, fallback = '') {
  if (typeof window === 'undefined') return fallback;
  return new URLSearchParams(window.location.search).get(name) ?? fallback;
}

// Screens are PATHS (the API's static serving falls back to index.html, so
// /trends deep-links survive a hard refresh in dev and in the deployed image).
const SCREENS = [
  { id: 'job', path: '/', label: 'Job view' },
  { id: 'pods', path: '/pods', label: 'Pod overview' },
  { id: 'trends', path: '/trends', label: 'Trends' },
  { id: 'incidents', path: '/incidents', label: 'Incidents' },
];
const screenForPath = (p) => (SCREENS.find((s) => s.path === p) || SCREENS[0]).id;

function sliceKey(region, environment) {
  return `${region}|${environment || 'all'}`;
}

export default function App() {
  // Full job catalog from the API; Region + Environment filter client-side by
  // id prefix (`ca-staging-…` ≠ `us-staging-…`) so the UI works against both
  // the region-aware API and the current preview deploy.
  const [allJobs, setAllJobs] = useState([]);
  const [regions, setRegions] = useState(DEFAULT_REGIONS);
  const [environments] = useState(DEFAULT_ENVIRONMENTS);
  const [region, setRegion] = useState(() => {
    // Deep-link rule: ?job= encodes its own locality (eu-staging-… IS eu) —
    // the job's region wins over an absent or contradicting ?region= so a
    // shared link never silently falls back to the us slice.
    const fromJob = param('job') ? regionOfJobId(param('job')) : null;
    const r = (fromJob || param('region') || 'us').toLowerCase();
    return DEFAULT_REGIONS.includes(r) ? r : 'us';
  });
  const [environment, setEnvironment] = useState(() => {
    // Default staging (primary pipeline). Empty string = all environments.
    // Same deep-link rule as region: the job id's environment prefix wins.
    const fromJob = param('job') ? environmentOfJobId(param('job')) : null;
    const e = (fromJob || param('environment') || 'staging').toLowerCase();
    if (e === 'all') return '';
    return DEFAULT_ENVIRONMENTS.includes(e) ? e : 'staging';
  });
  const [pod, setPod] = useState('');
  const [jobId, setJobId] = useState(() => param('job'));
  const [scope, setScope] = useState(() => param('scope'));
  const [runFilter, setRunFilter] = useState(() => param('run') || null);
  // "data as of HH:MM UTC" — set when a fetch RESOLVES, never on a timer (no
  // auto-refresh: the Refresh button and the filter changes are the only paths).
  const [asOf, setAsOf] = useState(null);
  // Which surface is on screen (job view / trends). Path-routed, back-button aware.
  const [screen, setScreen] = useState(() => screenForPath(window.location.pathname));
  // Theme precedence: explicit URL param > the user's saved choice > OS setting.
  const [mode, setMode] = useState(() => {
    const t = param('theme');
    if (t === 'light' || t === 'dark') return t;
    try {
      const saved = localStorage.getItem('mcip-theme');
      if (saved === 'light' || saved === 'dark') return saved;
    } catch { /* private mode */ }
    return currentMode();
  });

  const [summary, setSummary] = useState(null);
  const [clusters, setClusters] = useState(null);
  // MCIP-19 — active backend incidents (global, not per-job): fetched once on
  // load; the banner shows only when groups exist, the panel opens on demand.
  const [incidents, setIncidents] = useState(null);
  // Banner dismissal is per SESSION: a manager who has seen it shouldn't drag
  // a red strip through every page; the Incidents screen keeps the truth.
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem('incident-banner-dismissed') === '1'
  );
  // ?cluster= deep links (Slack/Jira "dashboard link") expand the row in place.
  // Old links may still carry ?view=human|developer — it is read nowhere and
  // harmless: the unified view IS the developer shape, which the API asserts is
  // a strict superset of the human one, so nothing a link pointed at is gone.
  const [expandedId, setExpandedId] = useState(() => param('cluster') || null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  // Bumped by the Retry button — re-runs the fetch effect with the same filters.
  const [retryTick, setRetryTick] = useState(0);
  // Drop out-of-order responses when the user clicks through runs quickly —
  // otherwise an older empty fetch can land after the latest and wipe good data.
  const fetchSeq = useRef(0);
  const prevFilters = useRef(null);
  // Deep-link run resolution (one shot per cluster id): ?cluster= links carry
  // no run, and the signal may live in an OLDER run than the default latest.
  const deepLinkTried = useRef(new Set());
  const preserveExpanded = useRef(false);
  const [deepLinkMiss, setDeepLinkMiss] = useState(null);
  // Last region|environment slice — detects a SWITCH (vs first mount) so deep
  // links with ?scope= survive load but never survive a slice change.
  const prevSlice = useRef(null);
  // Remember the last job picked per region|environment slice so filters restore
  // instead of always snapping back to the first catalog row (or a blank select).
  const lastJobBySlice = useRef({});

  // The theme toggle must beat the OS setting in both directions — and survive a
  // reload. It persists to localStorage, and if a stale ?theme= is sitting in the
  // URL it is rewritten to match, so the param can't silently undo the toggle.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    try { localStorage.setItem('mcip-theme', mode); } catch { /* private mode */ }
    const url = new URL(window.location.href);
    if (url.searchParams.get('theme') && url.searchParams.get('theme') !== mode) {
      url.searchParams.set('theme', mode);
      window.history.replaceState({}, '', url);
    }
  }, [mode]);

  // Back/forward must restore the screen the path names.
  useEffect(() => {
    const onPop = () => setScreen(screenForPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function navigate(nextScreen) {
    if (nextScreen === screen) return;
    const url = new URL(window.location.href);
    url.pathname = SCREENS.find((s) => s.id === nextScreen).path;
    // A screen switch drops the params the target cannot honour: ?run=/?cluster=
    // belong to the job view, ?weeks= to trends, ?window=/?pod= to incidents.
    if (nextScreen !== 'job') { url.searchParams.delete('run'); url.searchParams.delete('cluster'); }
    if (nextScreen !== 'trends') url.searchParams.delete('weeks');
    if (nextScreen !== 'incidents') { url.searchParams.delete('window'); url.searchParams.delete('pod'); }
    window.history.pushState({}, '', url);
    setScreen(nextScreen);
  }

  // One delegated listener feeds every card's cursor-tracking sheen (--mx/--my).
  // Cheaper than a handler per card, and it can never leak on re-render.
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const onMove = (e) => {
      const el = e.target.closest?.('.card, .tile');
      if (!el) return;
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mx', `${e.clientX - r.left}px`);
      el.style.setProperty('--my', `${e.clientY - r.top}px`);
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    return () => document.removeEventListener('pointermove', onMove);
  }, []);

  // Best-effort: incidents failing to load must never take the dashboard down —
  // the banner simply stays absent. Scoped to the ACTIVE slice and refetched on
  // a switch: a manager on US/staging must not see CA/EU incident counts.
  useEffect(() => {
    let cancelled = false;
    api.incidents({ windowHours: 168, region, environment })
      .then((r) => { if (!cancelled) setIncidents(r); })
      .catch(() => { if (!cancelled) setIncidents(null); });
    return () => { cancelled = true; };
  }, [region, environment]);

  useEffect(() => {
    let cancelled = false;
    // Fetch the full catalog so we can filter locally by id prefix — works
    // against APIs that do not yet expose ?region= / ?environment=.
    api.jobs()
      .then((d) => {
        if (cancelled) return;
        if (Array.isArray(d.regions) && d.regions.length) setRegions(d.regions);
        setAllJobs(d.jobs || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const jobs = useMemo(
    () => jobsForFilters(allJobs, { region, environment }),
    [allJobs, region, environment],
  );

  // When region/environment filters change (or jobs first land), keep pod/job
  // on a row that still belongs to the slice. Empty slices clear the selection
  // without leaving value="" on a later populated select.
  //
  // A slice SWITCH resets the whole page state, not just the job: a stale
  // ?scope= or ?run= from the previous region can only block the new slice's
  // data (a ca slice whose one job is api-scope renders empty under
  // scope=browser forever). Multiregion has FEWER jobs, so a carried-over
  // filter is usually fatal there — reset scope/run/expansion, keep only the
  // remembered job per slice.
  useEffect(() => {
    if (!allJobs.length) return;
    const key = sliceKey(region, environment);
    const sliceChanged = prevSlice.current !== null && prevSlice.current !== key;
    prevSlice.current = key;
    if (sliceChanged) {
      setScope('');
      setRunFilter(null);
      setExpandedId(null);
    }
    if (!jobs.length) {
      setPod('');
      setJobId('');
      setRunFilter(null);
      return;
    }
    const chosen = pickJobForRegion(jobs, {
      // A remembered/first job of the NEW slice must win over the previous
      // slice's jobId — passing a cross-slice jobId would keep it selected.
      jobId: sliceChanged ? null : jobId,
      rememberedJobId: lastJobBySlice.current[key],
    });
    if (!chosen) return;
    if (chosen.pod !== pod) setPod(chosen.pod);
    if (chosen.job_id !== jobId) {
      setJobId(chosen.job_id);
      setRunFilter(null);
    }
    lastJobBySlice.current[key] = chosen.job_id;
  }, [region, environment, jobs]);

  // Keep the address bar in step with the view. Region/environment update even
  // when jobId is empty (slice with no harvested jobs).
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('region', region);
    // 'all' is written explicitly so the all-environments view survives a
    // reload (an absent param would default back to staging).
    url.searchParams.set('environment', environment || 'all');
    url.searchParams.delete('view');
    if (jobId) url.searchParams.set('job', jobId);
    else url.searchParams.delete('job');
    if (scope) url.searchParams.set('scope', scope);
    else url.searchParams.delete('scope');
    if (runFilter) url.searchParams.set('run', runFilter);
    else url.searchParams.delete('run');
    window.history.replaceState({}, '', url);
  }, [jobId, scope, runFilter, region, environment]);

  useEffect(() => {
    if (!jobId) return;
    const filtersKey = `${jobId}|${scope || ''}|${runFilter || ''}`;
    // Changing job/scope/run drops an open signal — it can't belong to the new slice.
    // Skip on the first fetch so a ?cluster= deep link survives the initial load.
    // ALSO skip when the run change is the deep link's OWN resolution (a
    // ?cluster= link carries no run; the resolver below switches to the run
    // that actually shows the signal — clearing here would undo it).
    if (prevFilters.current !== null && prevFilters.current !== filtersKey) {
      if (preserveExpanded.current) {
        preserveExpanded.current = false;
      } else {
        setExpandedId(null);
        const url = new URL(window.location.href);
        url.searchParams.delete('cluster');
        window.history.replaceState({}, '', url);
      }
    }
    prevFilters.current = filtersKey;

    const seq = ++fetchSeq.current;
    setError(null);
    // Refetch keeps the frame: hold the previous render at reduced opacity rather
    // than flashing a skeleton and jumping the layout — but only apply the result
    // if this is still the newest request.
    setStale(true);
    Promise.all([
      api.summary(jobId, scope || null, runFilter),
      // Always the developer shape: the API asserts it is a strict superset of the
      // human one, so the unified view can render every human-lens element from
      // the same rows (summary, confidence, tests) with no second fetch.
      api.clusters(jobId, { view: 'developer', scope: scope || null, run: runFilter }),
    ])
      .then(([s, c]) => {
        if (seq !== fetchSeq.current) return;
        setSummary(s);
        setClusters(c);
        setAsOf(new Date());
        // Re-apply ?cluster= only when that signal is in the loaded list.
        const wanted = new URLSearchParams(window.location.search).get('cluster');
        if (wanted && c.clusters?.some((x) => x.cluster_id === wanted)) {
          setExpandedId(wanted);
        }
      })
      .catch((e) => {
        if (seq !== fetchSeq.current) return;
        setError(e.message);
      })
      .finally(() => {
        if (seq === fetchSeq.current) setStale(false);
      });
  }, [jobId, scope, runFilter, retryTick]);

  // Deep-link run resolution: the ?cluster= signal isn't in the loaded run's
  // list → look the cluster up, switch to the newest run that SHOWS it (one
  // attempt per id), or say honestly why it can't be shown. This is what makes
  // incident/Slack links to older-run signals land on the right view.
  useEffect(() => {
    if (!expandedId || !clusters || !summary) return undefined;
    if (clusters.clusters?.some((c) => c.cluster_id === expandedId)) {
      setDeepLinkMiss(null);
      return undefined;
    }
    if (deepLinkTried.current.has(expandedId)) return undefined;
    deepLinkTried.current.add(expandedId);
    let cancelled = false;
    api.cluster(expandedId)
      .then((detail) => {
        if (cancelled) return;
        const trendIds = [...(summary.trend || [])].reverse().map((r) => r.run_id);
        // The detail endpoint wraps the cluster ({job_id, run, cluster}) —
        // read the field where it lives, tolerant of either shape.
        const runsSeen = detail.cluster?.runs_seen ?? detail.runs_seen;
        const run = resolveRunForCluster(runsSeen, trendIds);
        if (run) {
          preserveExpanded.current = true;
          setRunFilter(run);
        } else {
          setDeepLinkMiss(`Signal ${expandedId} isn't in this job's stored runs — it may belong to a different job or have aged out.`);
        }
      })
      .catch(() => {
        if (!cancelled) setDeepLinkMiss(`Signal ${expandedId} was not found — it may have been re-clustered since the link was shared.`);
      });
    return () => { cancelled = true; };
  }, [expandedId, clusters, summary]);

  const selectedJob = useMemo(() => jobs.find((j) => j.job_id === jobId), [jobs, jobId]);

  // Pill availability from the full catalog — disabled where no jobs exist,
  // never hidden (board rule 3). Filtering itself stays on jobsForFilters.
  const locality = useMemo(() => localityOptions(allJobs), [allJobs]);

  function handlePod(nextPod) {
    setPod(nextPod);
    setRunFilter(null); // a run belongs to one job — the selection can't survive a switch
    const first = jobs.find((j) => j.pod === nextPod);
    if (first) {
      setJobId(first.job_id);
      lastJobBySlice.current[sliceKey(region, environment)] = first.job_id;
    }
  }

  function handleJob(nextJob) {
    setRunFilter(null);
    setJobId(nextJob);
    lastJobBySlice.current[sliceKey(region, environment)] = nextJob;
  }

  function handleRegion(nextRegion) {
    if (jobId) lastJobBySlice.current[sliceKey(region, environment)] = jobId;
    setRegion(nextRegion);
    setRunFilter(null);
  }

  function handleEnvironment(nextEnv) {
    if (jobId) lastJobBySlice.current[sliceKey(region, environment)] = jobId;
    setEnvironment(nextEnv);
    setRunFilter(null);
  }

  // Dropdown and chart click set the same selection. Null = latest (API default).
  const runsNewestFirst = summary ? [...summary.trend].reverse() : [];
  const effectiveRun = runFilter || summary?.run?.run_id || '';
  function pickRun(runId) {
    const latest = runsNewestFirst[0]?.run_id;
    setRunFilter(runId === latest ? null : runId);
  }

  // Deep-linkable signal: ?cluster=c1. MCIP-15's Slack message and MCIP-16's Jira
  // issue both cite a "dashboard link" — this is that link's target. The row
  // expands in place; the developer-view row already carries the whole detail
  // (fix card, summary, tests), so opening a signal never refetches.
  function openCluster(id) {
    if (expandedId === id) { closeCluster(); return; }
    const url = new URL(window.location.href);
    url.searchParams.set('cluster', id);
    window.history.replaceState({}, '', url);
    setExpandedId(id);
  }

  function closeCluster() {
    setExpandedId(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('cluster');
    window.history.replaceState({}, '', url);
  }

  if (loading) return <main className="page"><p className="notice">Loading…</p></main>;

  // One nav element, rendered by whichever screen owns the top bar right now.
  const topbarNav = (
    <PillGroup
      label="Screen"
      options={SCREENS.map((s) => s.id)}
      value={screen}
      onChange={navigate}
      format={(id) => SCREENS.find((s) => s.id === id).label}
    />
  );

  // Per-screen error isolation (browser-tested Aug 10): a crash inside ONE
  // screen must degrade to that screen's error card, never blank the whole
  // app — the root boundary in main.jsx stays as the last resort only.
  if (screen === 'trends') {
    return (
      <ErrorBoundary>
      <TrendsScreen
        jobs={jobs} jobId={jobId} onJob={handleJob}
        regions={locality.regions} region={region}
        onRegion={handleRegion}
        environments={locality.environments} environment={environment}
        onEnvironment={handleEnvironment}
        mode={mode} onMode={setMode} topbarNav={topbarNav}
      />
      </ErrorBoundary>
    );
  }

  if (screen === 'incidents') {
    return (
      <ErrorBoundary>
      <IncidentsScreen
        regions={locality.regions} region={region}
        onRegion={handleRegion}
        environments={locality.environments} environment={environment}
        onEnvironment={handleEnvironment}
        mode={mode} onMode={setMode} topbarNav={topbarNav}
      />
      </ErrorBoundary>
    );
  }

  if (screen === 'pods') {
    return (
      <ErrorBoundary>
      <PodOverviewScreen
        jobs={jobs}
        regions={locality.regions} region={region}
        onRegion={handleRegion}
        environments={locality.environments} environment={environment}
        onEnvironment={handleEnvironment}
        mode={mode} onMode={setMode} topbarNav={topbarNav}
        // The board's click-through: land on the job view pre-filtered to the
        // pod (its first job selected), exactly like using the Pod dropdown.
        onOpenPod={(podName) => { handlePod(podName); navigate('job'); }}
      />
      </ErrorBoundary>
    );
  }

  if (error && !summary) {
    return (
      <main className={`page${stale ? ' is-stale' : ''}`}>
        <p className="notice notice--error">
          Cannot reach the dashboard API: {error}
          <br />
          <span className="muted">
            The API may be restarting (deploys take ~a minute).{' '}
            <button type="button" className="ghost-btn" onClick={() => window.location.reload()}>Retry</button>
            {' '}· Local dev: <code>npm run api</code>
          </span>
        </p>
      </main>
    );
  }

  const selectedHasSignals = clusters ? clusters.count > 0 : true;

  return (
    <div className="app">
      {/* MCIP-19: slim red strip — only when active backend incidents exist.
          Clicking it opens the panel and jumps there. */}
      {!bannerDismissed && (
        <IncidentBanner
          incidents={incidents}
          region={region}
          environment={environment}
          // The banner is a DOOR, not a container (Aug 11 review: the inline
          // panel could neither close nor stop growing) — it navigates to the
          // incidents screen, which owns filtering, drafts, and detail. The
          // count was computed for a 7d window; the door must open on the SAME
          // view, so the window rides the URL (the screen reads it on mount).
          onView={() => {
            const url = new URL(window.location.href);
            url.searchParams.set('window', String(incidents?.window_hours || 168));
            window.history.replaceState({}, '', url);
            navigate('incidents');
          }}
          onDismiss={() => {
            sessionStorage.setItem('incident-banner-dismissed', '1');
            setBannerDismissed(true);
          }}
        />
      )}
      <TopBar
        jobs={jobs} pod={pod} jobId={jobId} scope={scope} mode={mode}
        region={region} regions={locality.regions}
        environment={environment} environments={locality.environments}
        runs={runsNewestFirst} selectedRun={effectiveRun}
        onPod={handlePod} onJob={handleJob} onScope={setScope} onRun={pickRun}
        onRegion={handleRegion} onEnvironment={handleEnvironment}
        onMode={setMode}
        asOf={asOf} refreshing={stale}
        onRefresh={() => setRetryTick((t) => t + 1)}
        nav={topbarNav}
      />

      <main className={`page${stale ? ' is-stale' : ''}`}>
        {/* A failed refetch must never masquerade as fresh data: the previous
            selection's numbers stay visible (better than a blank), but the
            banner sits at the TOP and says exactly what happened. */}
        {error && summary && (
          <p className="notice notice--error">
            Couldn't load {jobId}: {error} — the data below is from the previous
            selection.
            {' '}
            <button type="button" className="ghost-btn" onClick={() => setRetryTick((t) => t + 1)}>
              Retry
            </button>
          </p>
        )}

        {/* A ?cluster= link that cannot be honoured says so — never a silent
            "same page" (the ca-staging-fuel incident-link bug). */}
        {deepLinkMiss && (
          <p className="notice">
            {deepLinkMiss}
            {' '}
            <button type="button" className="ghost-btn" onClick={() => setDeepLinkMiss(null)}>Dismiss</button>
          </p>
        )}

        {summary && (
          <>
            <p className="context">
              {selectedJob?.workflow} · {summary.environment} · run {summary.run?.run_id}
              {/* MCIP-24: the release this run tested — named artifact + version,
                  as a chip so bug reports and eyes land on it. */}
              {summary.run?.deploy_version && (
                <>
                  {' '}·{' '}
                  {/* releaseLabel owns the artifact naming (and never double-
                      prefixes a version that already carries it); the shared
                      VersionChip owns the rendering. */}
                  <VersionChip
                    version={releaseLabel(summary.run.deploy_version, summary.scope)}
                    title="release at failure (deploy version this run tested)"
                  />
                </>
              )}
              {summary.run?.links?.allure_report && (
                <> · <a className="run-link" href={summary.run.links.allure_report} target="_blank" rel="noreferrer">Allure report ↗</a></>
              )}
              {summary.run?.links?.github_run && (
                <> · <a className="run-link" href={summary.run.links.github_run} target="_blank" rel="noreferrer">GitHub run ↗</a></>
              )}
              {/* Framework diff vs the previous run — "what did WE change" (a
                  range, never a person); present only when both runs carry SHAs. */}
              {summary.run?.framework_compare_url && (
                <> · <a className="run-link" href={summary.run.framework_compare_url} target="_blank" rel="noreferrer">test changes since last run ↗</a></>
              )}
            </p>

            <KpiRail summary={summary} />

            <div className="grid">
              <PassRateChart trend={summary.trend} mode={mode} selectedRun={effectiveRun} onPickRun={pickRun} />
              <div className="grid__side">
                <CategoryComposition categories={summary.category_composition} mode={mode} />
                <PodHotspot pods={summary.top_pods} mode={mode} />
              </div>
            </div>
          </>
        )}

        {clusters && clusters.count > 0 && (
          <MasterFixPlan jobId={jobId} runId={effectiveRun || null} clusterCount={clusters.count} />
        )}

        {clusters && (
          clusters.count === 0 ? (
            // An empty list is a real answer — but "all green" and "failures
            // exist, signals not harvested" are DIFFERENT answers, and showing
            // the same card for both hides real problems (browser-test finding).
            (() => {
              const shownRun = summary?.trend?.find((t) => t.run_id === effectiveRun) || summary?.trend?.[summary.trend.length - 1];
              const failedCount = shownRun?.failed ?? 0;
              const executed = shownRun?.executed ?? null;
              // Three different truths, three different messages: ran-nothing
              // (aborted before tests), all-green, and coverage-gap.
              const ranNothing = executed === 0;
              return (
                <section className="card empty">
                  <h2 className="card__title">
                    {ranNothing ? 'This run executed nothing'
                      : failedCount === 0 ? 'All green — no failures in this run'
                        : `No signals harvested for this run — ${failedCount} test${failedCount === 1 ? '' : 's'} failed`}
                  </h2>
                  <p className="card__subtitle">
                    {ranNothing
                      ? '0 tests executed — the job likely aborted before any test started (setup crash, runner failure, or a cancelled workflow). Check the GitHub Actions run log; there is nothing to cluster because nothing ran.'
                      : failedCount === 0
                        ? 'Every executed test passed, so there is nothing to cluster. This is the good kind of empty.'
                        : 'The failures are real (see the pass rate above) but failure-level signals were not harvested for this run — a coverage gap, not a clean bill. Harvest it from the ops panel (/admin) or pick a covered run.'}
                  </p>
                  {runFilter && (
                    <p className="card__subtitle" style={{ marginTop: 8 }}>
                      <button type="button" className="ghost-btn" onClick={() => setRunFilter(null)}>
                        Back to latest run
                      </button>
                    </p>
                  )}
                </section>
              );
            })()
          ) : (
            <>
              <Leaderboard
                runFilter={runFilter}
                onClearRun={() => setRunFilter(null)}
                clusters={clusters.clusters}
                mode={mode}
                onSelect={openCluster}
                selectedId={expandedId}
              />
              {/* Reconciliation: failures the tile counts but no signal above
                  covers — named where possible, honest about the rest. */}
              <UnaccountedFailures unaccounted={clusters.unaccounted} />
            </>
          )
        )}

      </main>
    </div>
  );
}
